import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";

import { Route, Routes } from "react-router-dom";

import Account from "./screens/Account";
import AppointmentTypes from "./screens/Schedules/Configure/AppointmentTypes";
import ApptCancellationReport from "./screens/Reports/AppointCancellationReport";
import Billing from "./screens/Billing";
import CareplanUpload from "./screens/Schedules/CareplanUpload";
import CaretagList from "./screens/CareManagement/Caretags/CaretagList";
import Chat from "./screens/Chat/Chat";
import CommandCenter from "./screens/CommandCenter";
import CommandCenterV2 from "./screens/CommandCenterV2";
import CommunityInfo from "./screens/CareManagement/Community/CommunityInfo";
import CommunityList from "./screens/CareManagement/Community/CommunityList";
import DailyMileageReport from "./screens/Reports/DailyMileageReport";
import Dashboard from "./screens/Analytics/Dashboard";
import Forgot from "./screens/Authentication/Forgot";
import GoogleCallback from "./screens/Authentication/GoogleCallback";
import MicrosoftCallback from "./screens/Authentication/MicrosoftCallback";
import Layout from "./Layout";
import LongitudinalSchedules from "./screens/LongitudinalSchedules";
import NewPatientsReport from "./screens/Reports/NewPatientReport";
import NoShowReport from "./screens/Reports/NoShowReport";
import OverduePatientsReport from "./screens/Reports/OverduePatientReport";
import PatientZipcodeReport from "./screens/Reports/PatientZipcodeReport";
import PatientInfo from "./screens/CareManagement/Patient/PatientInfo";
import PatientList from "./screens/CareManagement/Patient/PatientList";
import PatientSchedulingWizard from "./screens/PatientSchedulingWizard";
import PatientVisitReport from "./screens/Reports/PatientVisitReport";
import Pivot from "./screens/Analytics/Pivot";
import PricingPlans from "./screens/Authentication/common/PricingPlans";
import ProviderActivityReport from "./screens/Reports/ProviderActivityReport";
import ProviderInfo from "./screens/CareManagement/Provider/ProviderInfo";
import ProviderList from "./screens/CareManagement/Provider/ProviderList";
import ProvidersConfig from "./screens/Schedules/Configure/ProvidersConfig";
import React, { useState } from "react";
import ReportBuilder from "./screens/Analytics/ReportBuilder";
import RequireAuth from "./RequireAuth";
import RequireSystemAdmin from "./RequireSystemAdmin";
import useFaroUser from "./hooks/useFaroUser";
import useFaroRouteTracking from "./hooks/useFaroRouteTracking";
import RevenueReport from "./screens/Reports/RevenueReport";
import RunSchedulerMain from "./screens/Schedules/RunScheduler/RunScheduler";
import Signin from "./screens/Authentication/Signin";
import Signup from "./screens/Authentication/Signup";
import UserManagement from "./screens/UserManagement";
import VisitRules from "./screens/Schedules/Configure/VisitRules";
import Welcome from "./screens/Welcome";
import ZoneList from "./screens/CareManagement/Zones/ZonesList";
import { useEffect } from "react";
import PatientPlannerVisualizer from "./screens/PatientPlannerVisualizer";
import { LoadScript } from "@react-google-maps/api";
import MembersList from "./screens/CareManagement/Members/Members";
import ReferralSourcesList from "./screens/CareManagement/Referral Source/ReferralSourceList";
import SharedReport from "./screens/Analytics/SharedReport";
import SharedDashboard from "./screens/Analytics/SharedDashboard";
import CarePrograms from "./screens/Schedules/Configure/CarePrograms";
import ProgramFrequency from "./screens/Schedules/Configure/ProgramFrequency";
import AppointmentTypeCriteria from "./screens/Schedules/Configure/AppointmentTypeCriteria";
import RBACConfigurationPanel from "./screens/RbacConfigurationPanel";
import NoAccess from "./screens/NoAccess";

function App() {
  useFaroUser();
  useFaroRouteTracking();

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const handleScriptLoad = () => {
    setIsScriptLoaded(true);
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_API_KEY}
      onLoad={handleScriptLoad}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            theme: {
              primary: '#4aed88',
            },
          },
          error: {
            duration: 4000,
            theme: {
              primary: '#ff4b4b',
            },
          },
        }}
      />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="signup" element={<Signup />} />
          <Route path="signin" element={<Signin />} />
          <Route path="forgot" element={<Forgot />} />
          <Route path="no-access" element={<NoAccess />} />
          <Route path="/google">
            <Route path="callback" element={<GoogleCallback />} />
          </Route>
          <Route path="/microsoft">
            <Route path="callback" element={<MicrosoftCallback />} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route path="/schedules">
              <Route path="careplan-upload" element={<CareplanUpload />} />
              <Route path="run-scheduler" element={<RunSchedulerMain />} />
              <Route path="configure">
                <Route path="providers" element={<ProvidersConfig />} />
                <Route path="visit-rules" element={<VisitRules />} />
                <Route path="visit-types" element={<AppointmentTypes />} />
                <Route path="care-programs" element={<CarePrograms />} />
                <Route path="program-frequency" element={<ProgramFrequency />} />
                <Route path="appointment-type-criteria" element={<AppointmentTypeCriteria />} />
              </Route>
            </Route>
            <Route path="calendar-view" element={<LongitudinalSchedules />} />
            <Route element={<RequireSystemAdmin />}>
              <Route path="user-management" element={<UserManagement />} />
              <Route path="role-management" element={<RBACConfigurationPanel />} />
            </Route>

            <Route path="account" element={<Account />} />
            <Route path="billing" element={<Billing />} />
            <Route path="plans" element={<PricingPlans />} />
            <Route path="/home" element={<CommandCenterV2 />} />
            <Route
              path="/patient-panel-visualizer"
              element={
                <PatientPlannerVisualizer
                  isScriptLoaded={isScriptLoaded}
                  setIsScriptLoaded={setIsScriptLoaded}
                />
              }
            />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/data-management">
              <Route path="providers">
                <Route path="" element={<ProviderList />} />
                <Route path=":id" element={<ProviderInfo />} />
              </Route>
              <Route path="patients">
                <Route path="" element={<PatientList />} />
                <Route path=":id" element={<PatientInfo />} />
              </Route>
              <Route path="members">
                <Route path="" element={<MembersList />} />
              </Route>
              <Route path="communities">
                <Route path="" element={<CommunityList />} />
                <Route path=":id" element={<CommunityInfo />} />
              </Route>
              <Route path="caretags" element={<CaretagList />} />
              <Route path="zones" element={<ZoneList />} />
              <Route path="referral-sources">
                <Route path="" element={<ReferralSourcesList />} />
              </Route>
            </Route>
            <Route path="/chat" element={<Chat isFullScreen={true} />} />
            <Route
              path="patient-assignment"
              element={<PatientSchedulingWizard />}
            />
            <Route path="/reports">
              <Route
                path="patient-visit-report"
                element={<PatientVisitReport />}
              />
              <Route
                path="provider-activity-report"
                element={<ProviderActivityReport />}
              />
              <Route path="revenue-report" element={<RevenueReport />} />
              <Route
                path="appointment-cancellation-report"
                element={<ApptCancellationReport />}
              />
              <Route
                path="overdue-patients-report"
                element={<OverduePatientsReport />}
              />
              <Route
                path="new-patients-report"
                element={<NewPatientsReport />}
              />
              <Route path="no-show-report" element={<NoShowReport />} />
              <Route
                path="provider-daily-mileage-report"
                element={<DailyMileageReport />}
              />
              <Route
                path="patient-zipcode-report"
                element={<PatientZipcodeReport />}
              />
              <Route path="report-builder" element={<ReportBuilder />}></Route>
              <Route path="shared-reports/:id" element={<SharedReport />}></Route>
            </Route>
            <Route path="/dashboards">
              <Route path="shared-dashboards/:id" element={<SharedDashboard />} />
            </Route>
            <Route path="/analytics">
              <Route path="pivot" element={<Pivot />}></Route>
              <Route path="dashboard" element={<Dashboard />}></Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </LoadScript>
  );
}

export default App;
